<script lang="ts" setup>
import { ref, watch, computed } from 'vue';
import { useChatStore } from '../stores/chatStore';
import { ChatMode, type FileData } from '../utils/type';

const config = useRuntimeConfig();
const { t } = useI18n();
const chatStore = useChatStore();

const inputQuestion = ref<HTMLElement>();
const isSendButtonDisabled = ref(true);
const device = useDevice();
let isTablet = ref(false);
const fileInput = ref<HTMLInputElement>();
const selectedImage = ref<File | null>(null);
const imagePreviewUrl = ref<string | null>(null);
const shouldStickInputToBottom = computed(() =>
  chatStore.windowExtend ||
  chatStore.isNewWindow ||
  chatStore.isIframe ||
  device.isMobile
);
const maxInputLength = Number(config.public.inputMaxLength || 500);
const currentInputLength = ref(0);
const totalInputLength = computed(() => {
  const base = Number(chatStore.sessionQuestionLengthTotal ?? 0);
  const current = Number(currentInputLength.value ?? 0);
  if (!Number.isFinite(base) || !Number.isFinite(current)) {
    return 0;
  }
  return base + current;
});

onMounted(() => {
  isTablet.value = device.userAgent.includes('Macintosh') && 'ontouchend' in document;
});

// チャットモードが変更された時
watch(() => chatStore.chatMode, (newMode) => {
  if (newMode !== ChatMode.gomi && selectedImage.value) {
    clearImage();
  }
});

/**
 * 選択された画像をクリアする
 */
const clearImage = () => {
  selectedImage.value = null;
  imagePreviewUrl.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }

  // 質問文入力を再度有効化
  if (inputQuestion.value) {
    chatStore.uiTextQuestionInputPlaceholder = t('questionInputPlaceholder');
  }

  // テキストが空の場合は送信ボタンを無効化
  const text = inputQuestion.value?.innerText?.replace(/\n/g, '');
  currentInputLength.value = text?.length || 0;
  if (!text) {
    isSendButtonDisabled.value = true;
  }
}

const handleKeydown = (event: KeyboardEvent) => {
  if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
    event.preventDefault();
    if (!isSendButtonDisabled.value && !chatStore.isStreaming) {
      sendQuestion();
    }
  }
};

/**
 * 画像をリサイズする
 * @param file 元の画像ファイル
 * @param maxWidth 最大幅
 * @param maxHeight 最大高さ
 * @returns Promise<{resizedFile: File, dataUrl: string}>
 */
const resizeImage = (file: File): Promise<{ resizedFile: File, dataUrl: string }> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    const img = new Image();
    const maxWidth = config.public.imageMaxSize
    const maxHeight = config.public.imageMaxSize

    img.onload = () => {
      // 元のサイズを取得
      let { width, height } = img;

      // リサイズが必要かチェック
      if (width <= maxWidth && height <= maxHeight) {
        // リサイズ不要の場合、元のファイルとDataURLを返す
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            resizedFile: file,
            dataUrl: e.target?.result as string
          });
        };
        reader.readAsDataURL(file);
        return;
      }

      // アスペクト比を保持してリサイズ
      const aspectRatio = width / height;

      if (width > height) {
        // 横長の場合
        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }
      } else {
        // 縦長の場合
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }
      }

      // Canvasのサイズを設定
      canvas.width = width;
      canvas.height = height;

      // 画像を描画
      ctx.drawImage(img, 0, 0, width, height);

      // CanvasからBlobを作成
      canvas.toBlob((blob) => {
        if (blob) {
          // リサイズされたファイルを作成
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });

          // DataURLも取得
          const dataUrl = canvas.toDataURL(file.type, 0.9);

          resolve({
            resizedFile,
            dataUrl
          });
        }
      }, file.type, 0.9);
    };

    // 画像を読み込み
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * 質問入力時の処理
 */
const quetionInput = () => {
  if (!inputQuestion.value) {
    return;
  }

  let text = inputQuestion.value.innerText;
  text = text?.replace(/\n/g, '');

  const remainingLength = maxInputLength - Number(chatStore.sessionQuestionLengthTotal ?? 0);
  if (text.length > remainingLength) {
    const trimmedText = text.slice(0, Math.max(0, remainingLength));
    inputQuestion.value.innerText = trimmedText;
    text = trimmedText;

    // キャレットを末尾に移動
    const range = document.createRange();
    range.selectNodeContents(inputQuestion.value);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }

  currentInputLength.value = text.length;

  if (text) {
    isSendButtonDisabled.value = false;
  }
  else {
    isSendButtonDisabled.value = true;
  }
};

/**
 * 画像が選択された時の処理
 * @param event イベント
 */
const onImageSelected = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (file && file.type.startsWith('image/')) {
    try {
      // 画像をリサイズ
      const { resizedFile, dataUrl } = await resizeImage(file);

      selectedImage.value = resizedFile;
      imagePreviewUrl.value = dataUrl;

      // 画像選択時に質問文を空にし、入力を無効化
      if (inputQuestion.value) {
        inputQuestion.value.innerText = '';
        chatStore.uiTextQuestionInputPlaceholder = t('selectImagePlaceholder');
        currentInputLength.value = 0;
      }
      // 送信ボタンを有効化（画像があるため）
      isSendButtonDisabled.value = false;

      console.log('Original file size:', Math.round(file.size / 1024) + 'KB');
      console.log('Resized file size:', Math.round(resizedFile.size / 1024) + 'KB');
    } catch (error) {
      console.error('画像のリサイズに失敗しました:', error);
      selectedImage.value = null;
      imagePreviewUrl.value = null;
    }
  } else {
    selectedImage.value = null;
    imagePreviewUrl.value = null;
  }
}

/**
 * 質問を送信する
 */
const sendQuestion = () => {
  if (chatStore.isStreaming) {
    return;
  }

  const text = inputQuestion.value?.innerText;

  // 画像が選択されている場合は画像のみ送信
  if (selectedImage.value && imagePreviewUrl.value) {
    console.log('Sending image only:', selectedImage.value);

    // base64データからプレフィックスを除去（data:image/jpeg;base64, の部分を削除）
    const base64Data = imagePreviewUrl.value.split(',')[1];
    const fileType = selectedImage.value.type;

    // 画像データを含むオブジェクトを作成してchatStore.searchに渡す
    const fileData = <FileData>{
      base64: base64Data,
      fileType: fileType,
      fileUrl: imagePreviewUrl.value
    }
    console.log('Image data to send:', fileData);
    // TODO: chatStore.searchメソッドを更新して画像データを受け取れるようにする必要があります
    chatStore.search('', fileData); // 現在は空のテキストで送信
    clearImage();
  }
  else if (text) {
    // テキストがある場合のみテキスト送信
    chatStore.incrementSessionQuestionLength(currentInputLength.value);
    inputQuestion.value!.innerText = '';
    currentInputLength.value = 0;
    isSendButtonDisabled.value = true;
    chatStore.search(text, null);
  }
}

/**
 * 画像ファイルを選択する
 */
const selectImage = () => {
  fileInput.value?.click();
}
</script>

<template>
  <div class="w-full mt-3" :class="shouldStickInputToBottom ? 'mb-3' : 'mb-3'">
    <div class="flex items-center justify-center ">
      <div class="border border-design-gray w-[83%] rounded-[5px] p-2 bg-white"
        :class="{ 'sm:max-w-screen-md': chatStore.windowExtend }">
        <!-- 選択された画像のプレビュー -->
        <div v-if="selectedImage && chatStore.chatMode === ChatMode.gomi" class="mb-2 inline-block relative">
          <img v-if="imagePreviewUrl" :src="imagePreviewUrl" :alt="selectedImage.name"
            class="w-24 h-24 object-cover rounded-lg border border-gray-200">
          <button type="button" @click="clearImage"
            class="absolute -top-1 -right-1 w-5 h-5 bg-black bg-opacity-70 hover:bg-opacity-90 text-white rounded-full flex items-center justify-center text-sm transition-colors">
            <Icon name="material-symbols:close" size="13px" />
          </button>
        </div>
        <!-- 質問入力フィールド -->
        <div class="sc-user-input flex justify-center items-center">
          <div class="sc-user-input--text text-base placeholder:text-design-gray flex-1 pr-2"
            :placeholder="chatStore.uiTextQuestionInputPlaceholder" :contenteditable="selectedImage ? false : true"
            ref="inputQuestion" @input="quetionInput" @keydown="handleKeydown"
            :class="{ 'cursor-not-allowed': selectedImage, [chatStore.createLanguageClass(chatStore.language)]: true }">
          </div>
          <div class="flex">
            <button type="button" class="bg-design-red p-1 rounded-sm flex justify-center w-[32px] h-[27px]"
              @click="sendQuestion" :disabled="isSendButtonDisabled || chatStore.isStreaming" aria-label="送信">
              <Icon name="material-symbols:send-outline" size="18px" class="text-white" />
            </button>
          </div>
        </div>

        <div class="flex justify-end text-xs text-gray-500 mt-1">
          <span>{{ totalInputLength }} / {{ maxInputLength }}</span>
        </div>

        <!-- ツールエリア（ごみ分別モードの時のみ表示） -->
        <div v-if="chatStore.chatMode === ChatMode.gomi" class="flex items-center gap-2 mt-2 pt-2 min-h-8">
          <VTooltip :delay="10">
            <button type="button" class="tool-btn" @click="selectImage" :aria-label="$t('toolBtnImage')">
              <Icon name="material-symbols:attach-file" size="18px" />
            </button>

            <template #popper>
              {{ $t('toolBtnImage') }}
            </template>
          </VTooltip>
        </div>
        <input type="file" ref="fileInput" @change="onImageSelected" accept="image/*" class="hidden">
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "../assets/css/tailwind.css";

button:disabled {
  opacity: 0.5;
}

.sc-user-input {
  min-height: 20px;
  position: relative;
  bottom: 0;
  background-color: #ffff;
}

.sc-user-input--text {
  resize: none;
  border: none;
  outline: none;
  box-sizing: border-box;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
  -webkit-font-smoothing: antialiased;
  max-height: 200px;
  overflow: scroll;
  overflow-x: hidden;
  overflow-y: auto;
}

.sc-user-input--text:empty:before {
  content: attr(placeholder);
  display: block;
  filter: contrast(15%);
  outline: none;
  cursor: text;
}

.sc-user-input--text.cursor-not-allowed:before {
  cursor: not-allowed;
}

::-webkit-scrollbar {
  /* スクロールバー全体の幅 */
  width: 13px;

  /* スクロールバーの背景色 */
  background-color: none
}

::-webkit-scrollbar-thumb {
  /* スクロールバーの丸み */
  border-radius: 10px;

  /* スクロールバーの色 */
  box-shadow: inset 0 0 10px 10px #C3C3C3;

  /* スクロールバーの左右の余白 */
  border: solid 4px transparent;
}

.chat-mode-btn {
  @apply text-xs;
  @apply bg-white;
  @apply rounded-md;
  @apply border-solid;
  @apply border;
  @apply border-[#000000];
  @apply p-1;
  @apply hover:bg-default-btn-hover;
}

.chat-mode-btn.selected {
  @apply bg-[#000000cc];
  @apply text-white;
}

.tool-btn {
  @apply border;
  @apply border-transparent;
  @apply text-sm;
  @apply font-normal;
  @apply hover:bg-default-btn-hover;
  @apply flex;
  @apply items-center;
  @apply gap-1;
  @apply px-2;
  @apply py-1;
  @apply text-gray-600;
  @apply hover:text-gray-800;
  @apply rounded-md;
  @apply transition-colors;
}
</style>
